// DS: STU3.6
import type { FC } from 'react';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiGallection } from '@wix/da-papi-types';
import DndSortableList from '@wix/da-shared-react/pkg/Dnd/DndSortableList';
import { DraggableType } from '@wix/da-shared-react/pkg/types/dnd';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { useMeasureElementCookie } from '@wix/da-hooks/pkg/useMeasureElement/useMeasureElementCookie';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import {
  BiData,
  BiContextProvidedValue,
  ItemClickBiEvent,
  DefaultBiEvent,
} from '@wix/da-bi/pkg/events';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import Slider from '@wix/da-ds/pkg/Slider';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import Loader from '@wix/da-ds/pkg/Loader';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import CreateFolderCard from '../../_partials/CreateFolderCard';
import FolderCard from '../../_partials/FolderCard';

import s from './FolderListDesktop.scss';

export const THUMB_HEIGHT = 95;
export const THUMB_WIDTH = 200;
const THUMB_GAP = 16;

export interface Props {
  selectedFolder: PapiGallection;
  subfolders: PapiGallection[];
  visibleSubfolders: PapiGallection[];
  withCreateButton?: boolean;
  withFolderActions?: boolean;
  withHeader?: boolean;
  withDragToOrder?: boolean;
  expandedByDefault?: boolean;
  isBusy?: boolean;
  hasMoreSubfolders?: boolean;
  fetchMoreSubfolders: () => void;
  updateFolderOrder: (folder: PapiGallection, position: number) => void;
}

const FolderList: FC<Props> = ({
  expandedByDefault,
  selectedFolder,
  subfolders,
  visibleSubfolders,
  withCreateButton,
  withFolderActions,
  withHeader,
  withDragToOrder,
  isBusy,
  hasMoreSubfolders,
  fetchMoreSubfolders,
  updateFolderOrder,
}) => {
  const { t } = useTranslation();
  const { ref: containerRef, dimensions: measuredContainer } =
    useMeasureElementCookie(MeasuredCookieType.STASH_FOLDER_CARD, {
      cookieDim: 'width',
    });

  const containerWidth = useMemo(
    () => measuredContainer?.width,
    [measuredContainer?.width]
  );

  const thumbsPerRow = useMemo(() => {
    return Math.floor(containerWidth / (THUMB_WIDTH + THUMB_GAP));
  }, [containerWidth]);

  const gapWidth = THUMB_GAP * (thumbsPerRow - 1);

  const thumbWidth = useMemo(
    () => Math.floor((containerWidth - gapWidth) / thumbsPerRow),
    [containerWidth, gapWidth, thumbsPerRow]
  );

  const gridWidth = useMemo(
    () => thumbWidth * thumbsPerRow + gapWidth,
    [thumbsPerRow, thumbWidth, gapWidth]
  );

  const [isFolderListExpanded, expandFolderList] = useState(expandedByDefault);
  const canExpandFolders = visibleSubfolders.length > thumbsPerRow;

  const displayPositionToActualPosition = useCallback(
    (position: number) => {
      // Server re-ordering operates on all subfolders (including the hidden
      // ones) and on the front-end, we skip the hidden ones and only render
      // visibleSubfolders. DnD operates on visibleSubfolders, so we need to
      // transform the position (index) from the visibleSubfolders array into
      // the index of subfolders array. We do that by finding the folderId of
      // the visibleSubfolders element and find that same folderId in the
      // subfolders array. Since visibleSubfolders is a subset of subfolders,
      // we assume that all folderIds from visibleSubfolders exist in the
      // subfolders array.
      const afterFolderId = visibleSubfolders[position]?.folderId;
      return subfolders.findIndex(
        (folder: PapiGallection) => folder.folderId === afterFolderId
      );
    },
    [subfolders, visibleSubfolders]
  );

  const onFolderDragOrder = useCallback(
    (item, position) =>
      updateFolderOrder(item.folder, displayPositionToActualPosition(position)),
    [updateFolderOrder, displayPositionToActualPosition]
  );

  const sortableItems = useMemo(
    () => visibleSubfolders.map(folder => ({ id: folder.folderId, folder })),
    [visibleSubfolders]
  );

  if (visibleSubfolders.length < 1) {
    return null;
  }

  const renderFolderCard = (folder: PapiGallection) =>
    folder.isHidden ? null : (
      <FolderCard
        key={`folder-${folder.folderId}`}
        folder={folder}
        width={thumbWidth}
        height={THUMB_HEIGHT}
        withActions={withFolderActions}
        biData={BiData<ItemClickBiEvent>({
          evid: 100,
          view: BiContextProvidedValue,
          component: BiContextProvidedValue,
          page_typeid: BiContextProvidedValue,
          page_itemid: BiContextProvidedValue,
          sectionname: BiContextProvidedValue,
          typeid: ResourceType.STASH_FOLDER,
          itemid: folder.folderId,
        })}
      />
    );

  const folderCards = [
    withCreateButton && (
      <CreateFolderCard
        folderType="stash"
        key="folder-create"
        width={thumbWidth}
        height={THUMB_HEIGHT}
      />
    ),
    withDragToOrder ? (
      <DndSortableList
        key="folder-cards"
        dndType={DraggableType.STUDIO_FOLDER}
        items={sortableItems}
        onDragOrder={onFolderDragOrder}
      >
        {item => renderFolderCard(item.folder)}
      </DndSortableList>
    ) : (
      visibleSubfolders.map(folder => renderFolderCard(folder))
    ),
  ];

  return (
    <BiLoggerContextProvider value={{ sectionname: 'subfolders' }}>
      <div ref={containerRef}>
        <div style={{ width: gridWidth }}>
          {withHeader && (
            <div className={s['header']}>
              <Heading className={s['heading']}>
                {t('studio.folderList.heading')}
              </Heading>
              {canExpandFolders && (
                <TextButton
                  biData={
                    isFolderListExpanded
                      ? undefined
                      : BiData<DefaultBiEvent>({
                          evid: 2,
                          typeid: ResourceType.STASH_FOLDER,
                          itemid: selectedFolder.folderId,
                          click_info: 'stash_folder_subfolders_show_more',
                        })
                  }
                  className={s['more-folders-btn']}
                  size="small"
                  variant="main"
                  onClick={() => expandFolderList(!isFolderListExpanded)}
                >
                  {t(
                    isFolderListExpanded
                      ? 'studio.table.actions.showLess'
                      : 'studio.table.actions.showMore'
                  )}
                </TextButton>
              )}
            </div>
          )}

          <div className={s['content']}>
            {isFolderListExpanded ? (
              <div className={s['expanded-grid']}>{folderCards}</div>
            ) : (
              <Slider
                contentClassName={s['slider-content']}
                arrowSize={IconSize.TINY}
                permArrows
              >
                {folderCards}
              </Slider>
            )}

            {isFolderListExpanded && hasMoreSubfolders && !isBusy ? (
              <div className={s['load-more']}>
                <TextButton
                  size="medium"
                  variant="main"
                  onClick={fetchMoreSubfolders}
                >
                  {t('studio.folderList.loadMore')}
                </TextButton>
              </div>
            ) : null}

            {isBusy && (
              <Loader className={s['loader']} state="loading" size="big" />
            )}
          </div>
        </div>
      </div>
    </BiLoggerContextProvider>
  );
};
FolderList.displayName = 'FolderList';

export default FolderList;
